import { Song } from '../interfaces/userInterfaces';
import React, { createContext, useState, SetStateAction, Dispatch, useEffect } from 'react';

type MediaPlayerType = {
  currentSong: Song;
  playlist: Song[];
  isActive: boolean;
  clearPlaylist: () => void;
  changeTrackForward: () => void;
  changeTrackBackward: () => void;
  playNextTrack: (song: Song) => void;
  addToPlaylist: (song: Song) => void;
  isSongArray: (value: Song[]) => boolean;
  removeFromPlaylist: (song: Song) => void;
  setPlaylist: Dispatch<SetStateAction<Song[]>>;
  setCurrentSong: Dispatch<SetStateAction<Song>>;
};

export const MediaPlayerContext = createContext<MediaPlayerType>({} as MediaPlayerType);

export const MediaPlayerProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentSong, setCurrentSong] = useState<Song>({
    created_at: '',
    description: '',
    media: '',
    song_id: 0,
    title: '',
    user_id: '',
    username: '',
  });

  const [playlist, setPlaylist] = useState<Song[]>([
    {
      created_at: '',
      description: '',
      media: '',
      song_id: 0,
      title: '',
      user_id: '',
      username: '',
      index: 0,
    },
  ]);

  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    const playlistInStorage = localStorage.getItem('playlist');
    if (playlistInStorage) {
      setPlaylist(JSON.parse(playlistInStorage));
      setIsActive(true);
    }
  }, []);

  const updatePlaylistStateAndStorage = (newPlaylist: Song[]) => {
    setPlaylist(newPlaylist);
    localStorage.setItem('playlist', JSON.stringify(newPlaylist));
  };

  const isSongArray = (value: Song[]): value is Song[] => {
    if (value?.length > 0 && value[0].username && value[value.length - 1].username) {
      return true;
    } else {
      return false;
    }
  };

  const reindexList = (listToIndex: Song[]) => {
    return listToIndex.map((item, index) => {
      return { ...item, index: index };
    });
  };

  const generatePLaylist = (
    currentPlaylistState: Song[],
    dataToModify: Song,
    action: string
  ): Song[] => {
    switch (action) {
      case 'add':
        return reindexList([...currentPlaylistState, dataToModify]);

      case 'remove':
        return reindexList(
          currentPlaylistState.filter((track) => track.index !== dataToModify.index)
        );

      case 'shuffle':
        return currentPlaylistState;
      default:
        return currentPlaylistState;
    }
  };

  const addToPlaylist = (song: Song) => {
    if ((playlist.length === 1 && playlist[0].song_id === 0) || playlist[0].song_id === undefined) {
      updatePlaylistStateAndStorage(generatePLaylist([], song, 'add'));
      setCurrentSong({ ...song, index: 0 });
      setIsActive(true);
    } else {
      updatePlaylistStateAndStorage(generatePLaylist(playlist, song, 'add'));
    }
  };

  const removeFromPlaylist = (song: Song) => {
    if (song.index === currentSong.index && playlist.length > 1) {
      if (
        song.index !== undefined &&
        playlist[0].song_id !== undefined &&
        song.index !== playlist[0].index
      ) {
        setCurrentSong(playlist[song.index + 1]);
      } else {
        setCurrentSong(playlist[1]);
      }
    }

    if (playlist.length === 1) {
      clearPlaylist();
    } else {
      updatePlaylistStateAndStorage(generatePLaylist(playlist, song, 'remove'));
    }
  };

  const playNextTrack = (song: Song) => {
    if (currentSong.index !== undefined && currentSong.index + 1 < playlist.length) {
      setCurrentSong(playlist[currentSong.index + 1]);
    } else {
      setCurrentSong({} as Song);
    }
  };

  const changeTrackForward = () => {
    if (currentSong.index !== undefined && currentSong.index < playlist.length - 1) {
      setCurrentSong(playlist[currentSong.index + 1]);
    }
  };
  const changeTrackBackward = () => {
    if (currentSong.index !== undefined && currentSong.index !== 0) {
      setCurrentSong(playlist[currentSong.index - 1]);
    }
  };

  const clearPlaylist = () => {
    setPlaylist([{} as Song]);
    setCurrentSong({} as Song);
    setIsActive(false);
    localStorage.removeItem('playlist');
  };

  return (
    <MediaPlayerContext.Provider
      value={{
        playlist,
        isActive,
        currentSong,
        setPlaylist,
        isSongArray,
        addToPlaylist,
        playNextTrack,
        clearPlaylist,
        setCurrentSong,
        changeTrackForward,
        removeFromPlaylist,
        changeTrackBackward,
      }}
    >
      {children}
    </MediaPlayerContext.Provider>
  );
};
